/**
 * How an unbuilt destination is drawn — and it depends on how many there are.
 *
 * `components.md` → Shell → Unavailable destinations, RULED 2026-08-11 (Q5):
 *
 * > **Ghosting is a treatment for one row, not for four.**
 * > - **One pending destination:** ghost it — `--pp-text-ghost`, `soon` suffix.
 * >   WCAG's inactive-control exemption is doing exactly what it exists for.
 * > - **Two or more pending:** every pending row renders at `--pp-text-muted` and a
 * >   pill-size `inert` Surface reading `soon` carries the inertness. No exemption is
 * >   invoked.
 * > - **Never omit an unbuilt destination.** The sidebar is the product's map.
 *
 * The reasoning is worth keeping next to the code, because it is the part that makes the
 * rule reversible rather than arbitrary: at four rows of six, 2.07:1 stops reading as
 * *inert* and starts reading as a rendering fault. WCAG's exemption is written for a
 * disabled button among enabled ones, not for the majority of a navigation. **c40 shipped
 * four ghosted rows and was wrong about exactly this** — and the design's rule reverses
 * itself cleanly on its own: as destinations ship, pills disappear, and ghosting returns
 * the moment one is left.
 *
 * ## Why this is a function and not a ternary in the component
 *
 * One row is pending since U78 moved Import out, so the `ghost` branch is the one every
 * screen renders and `muted-pill` is the one no screen reaches until a destination is added
 * back. Both are pinned by a unit test on this function, whatever the sidebar holds.
 *
 * Extracted, both branches are pinned by a unit test today, and the rule is stated in one
 * place rather than implied by a rendering.
 */

/** How a pending row carries its inertness. */
export type PendingTreatment = "ghost" | "muted-pill";

/**
 * The treatment for a sidebar whose unbuilt destinations number `pendingCount`.
 *
 * Zero is `ghost` and the value is unused — with nothing pending there is no row to draw,
 * and returning the exemption branch for an empty set keeps the "one or fewer" boundary
 * in one place rather than making the caller special-case it.
 */
export function pendingTreatment(pendingCount: number): PendingTreatment {
  return pendingCount >= 2 ? "muted-pill" : "ghost";
}
